import {
   buildGroupedMediaObject,
   canGroupMessages,
   getMediaGroupId,
   splitGroupedMediaOnRecall,
} from '@/features/chats/utils/mediaGrouping';

import { i18n } from '@/localization/i18n';
import { logger } from '@/shared/utils';
import { deduplicateMessages } from '@/shared/utils/messageUtils';
import { createSlice } from '@reduxjs/toolkit';

// ─── Boundary grouping helpers ────────────────────────────────────────────────
/** True for UI-only items that carry no message data. */
const isUILabel = msg => msg.type === 'date-label' || msg.type === 'unread-separator';

/**
 * Newest leaf of a message or groupedMedia wrapper.
 * This is the anchor used by groupMediaMessages for time-window comparisons.
 */
const getNewestLeaf = msg => (msg?.type === 'groupedMedia' ? msg.messages?.[0] : msg);

/** Flatten a single message or groupedMedia into its raw children. */
const flattenMsg = msg => (msg?.type === 'groupedMedia' ? (msg.messages ?? []) : [msg]);

/**
 * Extract the shared mediaGroupId from either a groupedMedia wrapper
 * (top-level .mediaGroupId) or a single message (msgBody.media.mediaGroupId).
 */
const getGid = msg => msg?.mediaGroupId ?? getMediaGroupId(msg);
// ──────────────────────────────────────────────────────────────────────────────

const initialState = {
   data: {}, // { [chatId]: { messages: [], hasMoreOlder: bool, hasMoreNewer: bool } }
};

const findMessageInState = (messages, msgId) => {
   for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.msgId === msgId || msg.messageId === msgId) {
         return { message: msg, parentGroup: null, index: i };
      }
      if (msg.type === 'groupedMedia' && Array.isArray(msg.messages)) {
         const childIndex = msg.messages.findIndex(m => m.msgId === msgId || m.messageId === msgId);
         if (childIndex !== -1) {
            return { message: msg.messages[childIndex], parentGroup: msg, index: i, childIndex };
         }
      }
   }
   return { message: null, parentGroup: null, index: -1 };
};

const conversationSlice = createSlice({
   name: 'conversationData',
   initialState,
   reducers: {
      addMessages: (state, action) => {
         const { chatId, messages, hasMoreOlder = true, hasMoreNewer } = action.payload;
         if (!state.data[chatId]) {
            state.data[chatId] = { messages: [], hasMoreOlder: true, hasMoreNewer: false };
         }

         let prevMessages = state.data[chatId].messages || [];

         // Remove date-labels from existing that conflict with incoming ones
         const incomingDateKeys = new Set(messages.filter(m => m.type === 'date-label').map(m => m.dateKey));
         prevMessages = prevMessages.filter(m => !(m.type === 'date-label' && incomingDateKeys.has(m.dateKey)));

         // ── Cross-boundary grouping ──────────────────────────────────────────
         // Oldest existing (non-label) meets newest incoming (non-label).
         // Existing is NEWER than new batch → existing edge is the group anchor.
         let existingEdgeIdx = prevMessages.length - 1;
         while (existingEdgeIdx >= 0 && isUILabel(prevMessages[existingEdgeIdx])) existingEdgeIdx--;

         let newEdgeIdx = 0;
         while (newEdgeIdx < messages.length && isUILabel(messages[newEdgeIdx])) newEdgeIdx++;

         let workingPrev = prevMessages;
         let workingIncoming = messages;

         if (existingEdgeIdx >= 0 && newEdgeIdx < messages.length) {
            const edgeExisting = prevMessages[existingEdgeIdx];
            const edgeNew = messages[newEdgeIdx];

            // Phase A — mediaGroupId exact match: surgical merge
            const gidExisting = getGid(edgeExisting);
            const gidNew = getGid(edgeNew);

            if (gidExisting && gidNew && gidExisting === gidNew) {
               const merged = [...flattenMsg(edgeExisting), ...flattenMsg(edgeNew)];
               merged.sort((a, b) => b.timestamp - a.timestamp);
               workingPrev = [...prevMessages.slice(0, existingEdgeIdx), buildGroupedMediaObject(merged)];
               workingIncoming = [...messages.slice(0, newEdgeIdx), ...messages.slice(newEdgeIdx + 1)];
            } else {
               // Phase B — time-window 2-pointer
               // ref = newest leaf of existing boundary (existing is newer)
               const ref = getNewestLeaf(edgeExisting);
               const newBoundaryLeaf = getNewestLeaf(edgeNew);

               if (ref && newBoundaryLeaf && canGroupMessages(ref, newBoundaryLeaf)) {
                  // Walk pN: include more incoming messages that can group with ref
                  let pN = newEdgeIdx;
                  while (pN + 1 < messages.length && !isUILabel(messages[pN + 1])) {
                     const candidate = getNewestLeaf(messages[pN + 1]);
                     if (!candidate || !canGroupMessages(ref, candidate)) break;
                     pN++;
                  }

                  const combined = [
                     ...flattenMsg(edgeExisting),
                     ...messages.slice(newEdgeIdx, pN + 1).flatMap(flattenMsg),
                  ];
                  combined.sort((a, b) => b.timestamp - a.timestamp);

                  workingPrev = [...prevMessages.slice(0, existingEdgeIdx), buildGroupedMediaObject(combined)];
                  workingIncoming = [...messages.slice(0, newEdgeIdx), ...messages.slice(pN + 1)];
               }
            }
         }
         // ────────────────────────────────────────────────────────────────────

         state.data[chatId].messages = deduplicateMessages([...workingPrev, ...workingIncoming]);
         state.data[chatId].hasMoreOlder = hasMoreOlder;
         if (hasMoreNewer !== undefined) {
            state.data[chatId].hasMoreNewer = hasMoreNewer;
         }
      },
      clearMessages: (state, action) => {
         const { fromUserId } = action.payload;
         if (fromUserId) {
            state.data[fromUserId] = { messages: [], hasMoreOlder: false, hasMoreNewer: false };
         }
      },
      deleteChatHistory: (state, action) => {
         const { fromUserId } = action.payload;
         if (fromUserId) {
            delete state.data[fromUserId];
         }
      },
      clearInactiveChats: (state, action) => {
         const { activeChatId } = action.payload;
         Object.keys(state.data).forEach(chatId => {
            if (chatId !== activeChatId) {
               delete state.data[chatId];
            }
         });
      },
      prependMessages: (state, action) => {
         const { chatId, messages, hasMoreOlder, hasMoreNewer, stitchedMessageId } = action.payload;
         if (!state.data[chatId]) {
            state.data[chatId] = { messages: [], hasMoreOlder: false, hasMoreNewer: false };
         }

         let prevMessages = state.data[chatId].messages || [];

         // 1. If a stitch happened (messageDispatchService), remove the absorbed group
         if (stitchedMessageId) {
            prevMessages = prevMessages.filter(m => m.msgId !== stitchedMessageId);
         }

         // 2. Identify existing unread-separator and date-labels
         const hasExistingUnreadSeparator = prevMessages.some(m => m.type === 'unread-separator');
         const existingDateKeys = new Set(prevMessages.filter(m => m.type === 'date-label').map(m => m.dateKey));

         // 3. Filter incoming: omit duplicate label types
         const filteredIncoming = messages.filter(
            m =>
               !(m.type === 'unread-separator' && hasExistingUnreadSeparator) &&
               !(m.type === 'date-label' && existingDateKeys.has(m.dateKey)),
         );

         // ── Cross-boundary grouping (skip when stitch already handled it) ────
         // Oldest incoming (non-label) meets newest existing (non-label).
         // Incoming is NEWER than existing → incoming edge is the group anchor.
         let workingPrev = prevMessages;
         let workingIncoming = filteredIncoming;

         if (!stitchedMessageId && workingPrev.length > 0 && workingIncoming.length > 0) {
            let newEdgeIdx = workingIncoming.length - 1;
            while (newEdgeIdx >= 0 && isUILabel(workingIncoming[newEdgeIdx])) newEdgeIdx--;

            let existingEdgeIdx = 0;
            while (existingEdgeIdx < workingPrev.length && isUILabel(workingPrev[existingEdgeIdx])) existingEdgeIdx++;

            if (newEdgeIdx >= 0 && existingEdgeIdx < workingPrev.length) {
               const edgeNew = workingIncoming[newEdgeIdx]; // oldest incoming
               const edgeExisting = workingPrev[existingEdgeIdx]; // newest existing

               // Phase A — mediaGroupId exact match: surgical merge
               const gidNew = getGid(edgeNew);
               const gidExisting = getGid(edgeExisting);

               if (gidNew && gidExisting && gidNew === gidExisting) {
                  const merged = [...flattenMsg(edgeNew), ...flattenMsg(edgeExisting)];
                  merged.sort((a, b) => b.timestamp - a.timestamp);
                  workingIncoming = [...workingIncoming.slice(0, newEdgeIdx), buildGroupedMediaObject(merged)];
                  workingPrev = [...workingPrev.slice(0, existingEdgeIdx), ...workingPrev.slice(existingEdgeIdx + 1)];
               } else {
                  // Phase B — time-window 2-pointer
                  // ref = newest leaf of incoming boundary (new batch is newer)
                  const ref = getNewestLeaf(edgeNew);
                  const existingBoundaryLeaf = getNewestLeaf(edgeExisting);

                  if (ref && existingBoundaryLeaf && canGroupMessages(ref, existingBoundaryLeaf)) {
                     // Walk pE: include more existing messages that can group with ref
                     let pE = existingEdgeIdx;
                     while (pE + 1 < workingPrev.length && !isUILabel(workingPrev[pE + 1])) {
                        const candidate = getNewestLeaf(workingPrev[pE + 1]);
                        if (!candidate || !canGroupMessages(ref, candidate)) break;
                        pE++;
                     }

                     const combined = [
                        ...flattenMsg(edgeNew),
                        ...workingPrev.slice(existingEdgeIdx, pE + 1).flatMap(flattenMsg),
                     ];
                     combined.sort((a, b) => b.timestamp - a.timestamp);

                     workingIncoming = [...workingIncoming.slice(0, newEdgeIdx), buildGroupedMediaObject(combined)];
                     workingPrev = [...workingPrev.slice(0, existingEdgeIdx), ...workingPrev.slice(pE + 1)];
                  }
               }
            }
         }
         // ────────────────────────────────────────────────────────────────────

         // 4. Merge (incoming is newer → goes at the front)
         state.data[chatId].messages = deduplicateMessages([...workingIncoming, ...workingPrev]);

         if (hasMoreOlder !== undefined) state.data[chatId].hasMoreOlder = hasMoreOlder;
         if (hasMoreNewer !== undefined) state.data[chatId].hasMoreNewer = hasMoreNewer;
      },
      setConversationHasMore: (state, action) => {
         const { chatId, hasMoreOlder, hasMoreNewer } = action.payload;
         if (state.data[chatId]) {
            if (hasMoreOlder !== undefined) state.data[chatId].hasMoreOlder = hasMoreOlder;
            if (hasMoreNewer !== undefined) state.data[chatId].hasMoreNewer = hasMoreNewer;
         } else {
            state.data[chatId] = { messages: [], hasMoreOlder: false, hasMoreNewer: false };
         }
      },
      updateMessageStatus: (state, action) => {
         try {
            const { chatId, msgId, updatedConversationMessage } = action.payload;
            if (!state.data[chatId] || !updatedConversationMessage) return;

            const { message } = findMessageInState(state.data[chatId].messages, msgId);
            if (message) {
               Object.assign(message, updatedConversationMessage);
            }
         } catch (e) {
            logger.error('update status error,', e);
         }
      },

      updateMediaStatus: (state, action) => {
         const { chatId, msgId, is_downloaded, is_uploading, blobUrl } = action.payload;
         if (state.data[chatId]) {
            const { message } = findMessageInState(state.data[chatId].messages, msgId);
            if (message && message.msgBody?.media) {
               const media = message.msgBody.media;
               if (is_downloaded !== undefined) media.is_downloaded = is_downloaded;
               if (is_uploading !== undefined) media.is_uploading = is_uploading;
               if (blobUrl !== undefined) media.blobUrl = blobUrl;
            }
         }
      },
      deleteMessages: (state, action) => {
         const { chatId, msgIds } = action.payload;
         if (!state.data[chatId]) return;

         const deleteSet = new Set(msgIds);

         state.data[chatId].messages = state.data[chatId].messages.flatMap(msg => {
            if (deleteSet.has(msg.msgId)) return [];
            if (msg.type !== 'groupedMedia') return [msg];

            // Delete-for-me removes the item and keeps the rest as one group
            // (no split — the deleted image leaves no visible placeholder).
            const children = msg.messages.filter(m => !deleteSet.has(m.msgId));
            if (children.length === 0) return [];
            if (children.length === 1) return [children[0]];
            return [buildGroupedMediaObject(children)];
         });
      },
      setConversationMessages: (state, action) => {
         const { chatId, messages, hasMoreOlder, hasMoreNewer } = action.payload;
         if (!state.data[chatId]) {
            state.data[chatId] = { messages: [], hasMoreOlder: true, hasMoreNewer: false };
         }

         const existingMessages = state.data[chatId].messages ?? [];

         const selectedIds = new Set();
         for (const msg of existingMessages) {
            if (msg.type === 'groupedMedia') {
               msg.messages?.forEach(child => {
                  if (child.isSelected) selectedIds.add(child.msgId);
               });
            } else if (msg.isSelected) {
               selectedIds.add(msg.msgId);
            }
         }

         const localMediaMap = new Map();
         for (const leaf of existingMessages.flatMap(flattenMsg)) {
            const m = leaf?.msgBody?.media;
            if (m?.blobUrl && (m.is_downloaded === 1 || m.is_downloaded === 2)) {
               localMediaMap.set(leaf.msgId, m);
            }
         }

         const deduped = deduplicateMessages(messages);

         if (localMediaMap.size > 0) {
            for (const leaf of deduped.flatMap(flattenMsg)) {
               const saved = localMediaMap.get(leaf.msgId);
               if (saved && leaf.msgBody?.media) {
                  leaf.msgBody.media.blobUrl = saved.blobUrl;
                  leaf.msgBody.media.is_downloaded = saved.is_downloaded;
                  leaf.msgBody.media.is_uploading = saved.is_uploading;
               }
            }
         }

         if (selectedIds.size > 0) {
            for (const msg of deduped) {
               if (msg.type === 'groupedMedia') {
                  const children = msg.messages || [];
                  let allSelected = children.length > 0;
                  for (const child of children) {
                     if (selectedIds.has(child.msgId)) {
                        child.isSelected = true;
                     } else {
                        allSelected = false;
                     }
                  }
                  if (allSelected) msg.isSelected = true;
               } else if (selectedIds.has(msg.msgId)) {
                  msg.isSelected = true;
               }
            }
         }

         state.data[chatId].messages = deduped;

         if (hasMoreOlder !== undefined) state.data[chatId].hasMoreOlder = hasMoreOlder;
         if (hasMoreNewer !== undefined) state.data[chatId].hasMoreNewer = hasMoreNewer;
      },
      toggleMessageSelection: (state, action) => {
         const { chatId, msgId, selectionType } = action.payload;

         if (!state.data[chatId]?.messages) return;

         if (selectionType !== undefined) {
            state.data[chatId].selectionType = selectionType;
         }

         const messages = state.data[chatId].messages;
         const msg = messages.find(m => m.msgId === msgId);
         if (msg) {
            if (msg.type === 'groupedMedia') {
               const anyChildSelected = (msg.messages || []).some(c => c.isSelected);
               const next = !anyChildSelected;
               msg.isSelected = next;
               (msg.messages || []).forEach(c => {
                  c.isSelected = next;
               });
            } else {
               msg.isSelected = !msg.isSelected;
            }
            return;
         }

         const parent = messages.find(m => m.msgId !== msgId && typeof m.msgId === 'string' && m.msgId.includes(msgId));
         const child = parent?.messages?.find(c => c.msgId === msgId);
         if (child) {
            child.isSelected = !child.isSelected;
         }
      },
      resetSelectedMessages: (state, action) => {
         const { chatId } = action.payload;
         const chat = state.data[chatId];
         if (!chat?.messages) return;

         chat.selectionType = '';
         chat.messages = chat.messages.map(msg => {
            if (msg.type === 'groupedMedia') {
               const hasSelectedChild = (msg.messages || []).some(c => c.isSelected);
               if (!hasSelectedChild && !msg.isSelected) return msg;
               return {
                  ...msg,
                  isSelected: false,
                  messages: (msg.messages || []).map(c => (c.isSelected ? { ...c, isSelected: false } : c)),
               };
            }
            return msg.isSelected ? { ...msg, isSelected: false } : msg;
         });
      },
      recallMessages: (state, action) => {
         const { chatId, msgIds } = action.payload;
         const chat = state.data[chatId];
         if (!chat) return;

         const idSet = new Set(msgIds);

         state.data[chatId].messages = chat.messages.flatMap(msg => {
            if (msg.type === 'groupedMedia' && msg.messages) {
               // Unaffected group → leave as is
               if (!msg.messages.some(m => idSet.has(m.msgId))) return [msg];

               // Split the group at each recalled position; the recalled child is
               // injected as a top-level "You deleted this message" placeholder
               // between the two resulting groups.
               return splitGroupedMediaOnRecall(msg.messages, idSet);
            }

            return idSet.has(msg.msgId) ? [{ ...msg, deleteStatus: 1 }] : [msg];
         });
      },
      recalculateUnreadMessageLabel: (state, action) => {
         const { chatId, unreadMessageId, unreadCount, isViewing = false } = action.payload;

         const chat = state.data[chatId];
         if (!chat?.messages) return;

         if (isViewing) {
            // Find existing unread separator and update its label
            const separatorIndex = chat.messages.findIndex(msg => msg.type === 'unread-separator');
            if (separatorIndex !== -1) {
               chat.messages[separatorIndex] = {
                  ...chat.messages[separatorIndex],
                  label: i18n.t(
                     unreadCount === 1 ? 'message.UNREAD_MESSAGE_SINGULAR' : 'message.UNREAD_MESSAGE_PLURAL',
                  ),
               };
            }
            return;
         }

         // Remove any existing unread separators
         let finalMessages = chat.messages.filter(msg => msg.type !== 'unread-separator');

         // If there are unread messages, insert separator before the first unread message
         if (unreadCount > 0 && unreadMessageId) {
            const firstUnreadIndex = finalMessages.findIndex(msg => msg.msgId === unreadMessageId);

            if (firstUnreadIndex !== -1) {
               const unreadSeparator = {
                  type: 'unread-separator',
                  label: i18n.t(
                     unreadCount === 1 ? 'message.UNREAD_MESSAGE_SINGULAR' : 'message.UNREAD_MESSAGE_PLURAL',
                  ),
                  msgId: `${unreadMessageId}-unread-separator`,
               };

               // Insert the separator right before the first unread message
               finalMessages.splice(firstUnreadIndex, 0, unreadSeparator);
            }
         }

         state.data[chatId].messages = finalMessages;
      },
      removeUnreadLabel: (state, action) => {
         const { chatId } = action.payload;
         if (state.data[chatId]) {
            state.data[chatId].messages = state.data[chatId].messages.filter(msg => msg.type !== 'unread-separator');
         }
      },
      setHighlightedMessageId: (state, action) => {
         const { chatId, msgId } = action.payload;
         if (state.data[chatId]) {
            const { message, parentGroup } = findMessageInState(state.data[chatId].messages, msgId);
            if (message) {
               message.isHighlighted = true;
               // If it's in a group, we might want the group to show it's focused
               if (parentGroup) parentGroup.isHighlighted = true;
            }
         }
      },
      clearHighlightedMessageId: (state, action) => {
         const { chatId, msgId } = action.payload;
         if (state.data[chatId]) {
            const { message, parentGroup } = findMessageInState(state.data[chatId].messages, msgId);
            if (message) {
               message.isHighlighted = false;
               if (parentGroup) parentGroup.isHighlighted = false;
            }
         }
      },

      updateMessage: (state, action) => {
         const { chatId, msgId, msgBody, msgStatus, editMessageId, editedStatus, deliveryAndReadInfo } = action.payload;

         if (state.data[chatId]) {
            const { message } = findMessageInState(state.data[chatId].messages, msgId);
            if (message) {
               if (msgBody) message.msgBody = { ...message.msgBody, ...msgBody };
               if (msgStatus !== undefined) message.msgStatus = msgStatus;
               if (editedStatus !== undefined) message.editedStatus = editedStatus;
               message.editMessageId = editMessageId;

               if (deliveryAndReadInfo !== undefined) {
                  message.deliveryAndReadInfo = deliveryAndReadInfo;
               }
            }
         }
      },
      updateMessageFavouriteStatus: (state, action) => {
         const { chatId, msgId, favouriteStatus } = action.payload;
         if (state.data[chatId]) {
            const { message } = findMessageInState(state.data[chatId].messages, msgId);
            if (message) message.favouriteStatus = favouriteStatus;
         }
      },
      /**
       * Replace an existing message (by msgId) with `message`, preserving the
       * local media object when local has a blobUrl + is_downloaded of 1 or 2.
       * Used by messageBuffer flush to overwrite stale store data with fresh
       * buffered data without clobbering an already-downloaded media payload.
       */
      mergeMessageById: (state, action) => {
         const { chatId, message } = action.payload;
         const chat = state.data[chatId];
         if (!chat || !message?.msgId) return;

         const { message: existing, parentGroup, index, childIndex } = findMessageInState(chat.messages, message.msgId);
         if (!existing) return;

         const existingMedia = existing?.msgBody?.media;
         const protectMedia =
            existingMedia &&
            existingMedia.blobUrl &&
            (existingMedia.is_downloaded === 1 || existingMedia.is_downloaded === 2);

         const merged = {
            ...message,
            msgBody: {
               ...message.msgBody,
               ...(protectMedia ? { media: { ...message.msgBody?.media, ...existingMedia } } : {}),
            },
         };

         if (parentGroup) {
            parentGroup.messages[childIndex] = merged;
         } else {
            chat.messages[index] = merged;
         }
      },
   },
});

export const {
   addMessages,
   clearMessages,
   deleteChatHistory,
   clearInactiveChats,
   prependMessages,
   setConversationHasMore,
   updateMessageStatus,
   updateMediaStatus,
   deleteMessages,
   recallMessages,
   setConversationMessages,
   toggleMessageSelection,
   resetSelectedMessages,
   recalculateUnreadMessageLabel,
   removeUnreadLabel,
   setHighlightedMessageId,
   clearHighlightedMessageId,
   updateMessage,
   updateMessageFavouriteStatus,
   mergeMessageById,
} = conversationSlice.actions;

export const { reducer: conversationReducer } = conversationSlice;
